import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const hdr = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
const fmt = (o) => (o && typeof o === 'object' ? Object.entries(o).map(([k, v]) => `${k}=${v === null ? 'null' : v}`).join(', ') : String(o ?? '—'));

// Round 74 item 9: change records (before/after) with an owner-only, TOTP step-up "reverse" for admin role/status/reports_to changes only.
export const ChangeRecordsCard = ({ token }) => {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ reason: '', totp_code: '' });
  const [done, setDone] = useState(null);
  const load = async () => {
    setErr(null);
    try { setRows((await axios.get(`${API_URL}/api/admin/activity/changes?limit=30`, hdr(token))).data.changes); }
    catch (e) { setErr(e.response?.data?.detail || 'Could not load change records'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);
  const reverse = async () => {
    setBusy(true); setErr(null); setDone(null);
    try {
      const r = await axios.post(`${API_URL}/api/admin/activity/${sel}/reverse`, form, hdr(token));
      setDone(r.data); setSel(null); setForm({ reason: '', totp_code: '' }); await load();
    } catch (e) { setErr(e.response?.data?.detail || 'Reverse refused'); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-border/60 p-5 mb-6" data-testid="change-records-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">Change records</h3>
          <p className="text-xs text-muted-foreground">Admin actions that recorded a before/after. Reverse (owner + fresh authenticator code) is offered only for administrator role / status / reports_to changes; deletion, retention, holds, backups, exports, e-mails, the gate and all configuration are refused. A reverse never deletes data.</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="change-records-refresh-btn">Refresh</Button>
      </div>
      {err && <p className="text-sm text-red-700 mt-2" data-testid="change-records-error">{err}</p>}
      {done && <p className="text-sm text-green-800 mt-2" data-testid="change-records-done">Reversed seq {done.seq} on admin {done.target}: {fmt(done.after)} (recorded {done.reversed_at?.slice(0, 16)}Z)</p>}
      {rows && rows.length === 0 && <p className="text-sm text-muted-foreground mt-3" data-testid="change-records-empty">No change records yet.</p>}
      {rows && rows.length > 0 && (
        <div className="mt-3 space-y-2 text-xs">
          {rows.map((r) => (
            <div key={r.seq} className="rounded-lg border border-border/60 p-2" data-testid={`change-record-${r.seq}`}>
              <p className="font-mono">#{r.seq} · {r.at?.slice(0, 16)}Z · {r.action} · target {r.target || '—'} · {r.outcome}</p>
              <p>before: <span className="font-mono">{fmt(r.before)}</span> → after: <span className="font-mono">{fmt(r.after)}</span>{r.reason && <> · reason: {r.reason}</>}</p>
              {r.assessment?.reversible ? (
                sel === r.seq ? (
                  <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`reverse-form-${r.seq}`}>
                    <span>Would set <span className="font-mono">{fmt(r.assessment.would_set)}</span></span>
                    <input className="border rounded px-2 py-1 flex-1 min-w-[12rem]" placeholder="Reason (at least 8 characters)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="reverse-reason-input" />
                    <input className="border rounded px-2 py-1 w-28" placeholder="Authenticator code" value={form.totp_code} onChange={(e) => setForm({ ...form, totp_code: e.target.value })} data-testid="reverse-totp-input" />
                    <Button size="sm" variant="destructive" onClick={reverse} disabled={busy} data-testid="reverse-confirm-btn">Reverse now</Button>
                    <Button size="sm" variant="ghost" onClick={() => setSel(null)} data-testid="reverse-cancel-btn">Cancel</Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" className="mt-2" onClick={() => setSel(r.seq)} data-testid={`reverse-open-${r.seq}`}>Reverse…</Button>
                )
              ) : (
                <p className="text-muted-foreground mt-1" data-testid={`not-reversible-${r.seq}`}>Not reversible: {r.assessment?.refusal}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
